import { useCallback, useRef, useState, useEffect } from 'react'

const useLongPress = (onLongPress, onClick, { shouldPreventDefault = true, delay = 300 } = {}) => {
  const [longPressTriggered, setLongPressTriggered] = useState(false)
  const timeout = useRef()
  const target = useRef()

  const start = useCallback(
    (event) => {
      if (shouldPreventDefault && event.target) {
        target.current = event.target
        // Add event listener for touchend to handle prevent default behavior
        target.current.addEventListener('touchend', preventDefault, { passive: false })
      }
      timeout.current = setTimeout(() => {
        onLongPress(event)
        setLongPressTriggered(true)
      }, delay)
    },
    [onLongPress, delay, shouldPreventDefault]
  )

  const clear = useCallback(
    (event, shouldTriggerClick = true) => {
      clearTimeout(timeout.current)
      if (shouldTriggerClick && !longPressTriggered) {
        onClick()
      }
      setLongPressTriggered(false)
      if (shouldPreventDefault && target.current) {
        target.current.removeEventListener('touchend', preventDefault)
      }
    },
    [shouldPreventDefault, onClick, longPressTriggered]
  )

  // Cleanup on unmount or when target changes
  useEffect(() => {
    return () => {
      if (target.current) {
        target.current.removeEventListener('touchend', preventDefault)
      }
      clearTimeout(timeout.current)
    }
  }, [])

  return {
    onMouseDown: start,
    onTouchStart: start,
    onMouseUp: (e) => clear(e),
    onMouseLeave: (e) => clear(e, false),
    onTouchEnd: (e) => clear(e)
  }
}

const isTouchEvent = (event) => {
  return 'touches' in event
}

const preventDefault = (event) => {
  if (!isTouchEvent(event)) return

  // Prevent default for single touch only
  if (event.touches.length < 2 && event.preventDefault) {
    event.preventDefault()
  }
}

export default useLongPress
